import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { WherebyDedicatedCallKind } from '../../../../pure-js/enums/Whereby'

/** A participant currently connected to the team's Whereby room. `userId` comes from Whereby `externalId`. */
export interface WherebyParticipant {
  userId: string | null
  displayName: string
}

/** The activity a call is pinned to, so the room survives refresh and doesn't drift with the schedule. */
export interface CallScope {
  kind: WherebyDedicatedCallKind
  sprintId: string
  activityId: string
  teamId: string
  activityType: string
  // The game session this call belongs to. Lets us watch that session doc and leave when it completes.
  sessionId?: string
}

interface WherebyState {
  isCallActive: boolean
  isCallMinimized: boolean
  floatingButtonPosition: { x: number; y: number }
  reconnectPending: boolean
  participants: WherebyParticipant[]
  callScope: CallScope | null
}

export const WHEREBY_SESSION_KEY = 'whereby_call_state'
const SESSION_MAX_AGE_MS = 4 * 60 * 60 * 1000 // 4 hours

function readSessionState(): Pick<WherebyState, 'isCallActive' | 'isCallMinimized' | 'callScope'> | null {
  try {
    const raw = sessionStorage.getItem(WHEREBY_SESSION_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as {
      isCallActive: boolean
      isCallMinimized: boolean
      startedAt: number
      callScope?: CallScope | null
    }
    if (!parsed.isCallActive) return null
    if (Date.now() - parsed.startedAt > SESSION_MAX_AGE_MS) {
      sessionStorage.removeItem(WHEREBY_SESSION_KEY)
      return null
    }
    return { isCallActive: true, isCallMinimized: Boolean(parsed.isCallMinimized), callScope: parsed.callScope ?? null }
  } catch {
    return null
  }
}

const sessionState = typeof window !== 'undefined' ? readSessionState() : null

const getDefaultPosition = (): { x: number; y: number } => {
  if (typeof window !== 'undefined') {
    return { x: 16, y: window.innerHeight - 220 }
  }
  return { x: 16, y: 16 }
}

const initialState: WherebyState = {
  isCallActive: sessionState?.isCallActive ?? false,
  isCallMinimized: sessionState?.isCallMinimized ?? false,
  floatingButtonPosition: getDefaultPosition(),
  reconnectPending: false,
  participants: [],
  callScope: sessionState?.callScope ?? null
}

const wherebySlice = createSlice({
  name: 'whereby',
  initialState,
  reducers: {
    setCallActive: (state, action: PayloadAction<boolean>) => {
      state.isCallActive = action.payload
      if (!action.payload) {
        state.isCallMinimized = false
        state.callScope = null // leaving the call clears its pinned activity
      }
    },
    setCallScope: (state, action: PayloadAction<CallScope | null>) => {
      state.callScope = action.payload
    },
    setCallMinimized: (state, action: PayloadAction<boolean>) => {
      state.isCallMinimized = action.payload
    },
    setFloatingPosition: (state, action: PayloadAction<{ x: number; y: number }>) => {
      state.floatingButtonPosition = action.payload
    },
    setReconnectPending: (state, action: PayloadAction<boolean>) => {
      state.reconnectPending = action.payload
    },
    setWherebyParticipants: (state, action: PayloadAction<WherebyParticipant[]>) => {
      state.participants = action.payload
    }
  }
})

export const {
  setCallActive,
  setCallMinimized,
  setFloatingPosition,
  setReconnectPending,
  setWherebyParticipants,
  setCallScope
} = wherebySlice.actions
export default wherebySlice.reducer
